import React from "react";
import { ArrowRight, Clock, CheckCircle, ArrowLeft } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import QuoteComparisonReport from "../components/QuoteComparisonReport";
import {
  getQuoteRequest,
  getAnalysis,
  transformAnalysisData,
} from "../services/rateAnalysis";

const EXAMPLE_REQUEST_ID = "876efc16-60e4-4658-843e-5f8856acbf13";

const ExampleResults: React.FC = () => {
  const navigate = useNavigate();
  // Fetch all necessary data in parallel with a different query key
  const { isLoading, data } = useQuery(
    ["exampleQuoteData", EXAMPLE_REQUEST_ID],
    async () => {
      const [quoteRequest, analysis] = await Promise.all([
        getQuoteRequest(EXAMPLE_REQUEST_ID),
        getAnalysis(EXAMPLE_REQUEST_ID),
      ]);

      if (analysis) {
        const transformedAnalysis = transformAnalysisData(
          analysis,
          quoteRequest
        );
        return { quoteRequest, analysis: transformedAnalysis };
      }

      return { quoteRequest, analysis: null };
    },
    {
      retry: false,
      staleTime: Infinity, // Since this is example data, we don't need to refetch
    }
  );

  // If we're still loading, show a loading state
  if (isLoading) {
    return (
      <div className="py-12 px-4">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If we have no data, show an error state
  if (!data?.analysis || !data?.quoteRequest) {
    return (
      <div className="py-12 px-4">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
              Example Not Available
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              We're sorry, but the example analysis is not available at the
              moment.
            </p>
            <Link
              to="/check-my-rate"
              className="btn-primary inline-flex items-center justify-center space-x-2"
            >
              <span>Get Your Quote Analysis</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 px-4">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
              Example Quote Analysis
            </h1>
            <p className="text-xl text-gray-600">
              This is an example of what you'll receive when you submit your
              quote
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Based on actual quotes from{" "}
              {new Date(data.analysis.created_at).toLocaleDateString()}
            </p>
          </div>

          <QuoteComparisonReport
            analysis={data.analysis}
            quoteRequest={data.quoteRequest}
            isExample={true}
            gradeTitle="Example Quote Grade"
          />

          <div className="mt-8 flex justify-between">
            <button
              onClick={() => navigate(-1)}
              className="btn-secondary inline-flex items-center justify-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span>Back</span>
            </button>
            <Link
              to="/check-my-rate"
              className="btn-primary inline-flex items-center justify-center space-x-2"
            >
              <span>Get Your Quote Analysis</span>
              <ArrowRight size={18} />
            </Link>
          </div>

          {/* Additional Information */}
          <div className="mt-12 bg-primary-light/10 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <Clock className="h-6 w-6 text-primary mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Real Results, Real Fast
                </h3>
                <p className="text-gray-700">
                  When you submit your quote, you'll receive your personalized
                  analysis within 24 hours. Our mortgage experts review each
                  quote to provide you with accurate, actionable
                  recommendations.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-start">
                    <CheckCircle size={18} className="text-primary mr-2 mt-1" />
                    <span className="text-gray-700">
                      Compare against current market rates
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle size={18} className="text-primary mr-2 mt-1" />
                    <span className="text-gray-700">
                      Get personalized recommendations
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle size={18} className="text-primary mr-2 mt-1" />
                    <span className="text-gray-700">
                      See alternative quotes from local lenders
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExampleResults;
